import { LE, LE_CODES } from "@/constants/enums";
import { getElectronicFormRouteName } from "@/helpers/electronicFormHelpers";
import type { StandardFormRouteNames } from "@/views/electronicForm/electronicForm";
import { createWorksheetChildRoutes } from "@/views/electronicForm/worksheets/worksheetRoutes";
import { EntityAudit } from "@modernary/mui/components/audit";
import { getLookupsFromLookupGroup } from "@modernary/mui/components/lookup";
import { RouterView, type RouteRecordRaw, type RouterOptions } from "vue-router";

// Reusable HOC for dynamic imports with props
const withProps = (importFn: () => Promise<any>, defaultProps: Record<string, any> = {}) => {
  return {
    component: importFn,
    props: defaultProps,
  };
};

// Troubleshoot your routes using this - https://paths.esm.dev

const openBurningForms: Array<keyof Pick<typeof LE_CODES.ElectronicForm, "ef069">> = ["ef069"] as const;

const nameGenerator = (acc: any, form: (typeof openBurningForms)[number]) => {
  if (!acc[form]) acc[form] = {};

  const current = acc[form];
  current.new = getElectronicFormRouteName(LE_CODES.ElectronicForm[form], "New");
  current.view = getElectronicFormRouteName(LE_CODES.ElectronicForm[form], "View");
  current.edit = getElectronicFormRouteName(LE_CODES.ElectronicForm[form], "Edit");
  current.base = getElectronicFormRouteName(LE_CODES.ElectronicForm[form], "Base");
  current.audit = getElectronicFormRouteName(LE_CODES.ElectronicForm[form], "Audit");

  current.initiateSubmission = getElectronicFormRouteName(LE_CODES.ElectronicForm[form], "InitiateSubmission");
  current.submissionAgreementForPdf = getElectronicFormRouteName(
    LE_CODES.ElectronicForm[form],
    "SubmissionAgreementForPdf",
  );
  current.reviewSubmission = getElectronicFormRouteName(LE_CODES.ElectronicForm[form], "ReviewSubmission");
  current.repudiateSubmission = getElectronicFormRouteName(LE_CODES.ElectronicForm[form], "RepudiateSubmission");

  current.worksheets = getElectronicFormRouteName(LE_CODES.ElectronicForm[form], "Worksheets");
  current.documents = getElectronicFormRouteName(LE_CODES.ElectronicForm[form], "Documents");

  return acc;
};

// Explicitly defined names go here
const staticNames = {
  openBurning: "ElectronicForm.OpenBurning",
  openBurningList: "ElectronicForm.OpenBurningList",
  openBurningSelect: "ElectronicForm.OpenBurningSelect",

  // ef001: {
  //   initiateSubmission: "ElectronicForm.ef001InitiateSubmission",
  //   reviewSubmission: "ElectronicForm.ef001ReviewSubmission",
  //   repudiateSubmission: "ElectronicForm.ef001RepudiateSubmission",
  // },
};

type StaticOpenBurningNames = typeof staticNames;

type GeneratedOpenBurningNames = {
  [K in (typeof openBurningForms)[number]]: StandardFormRouteNames;
};

// Combined type for all route names -
type OpenBurningRouteNames = StaticOpenBurningNames & GeneratedOpenBurningNames;

// Dynamically generate all common names, starting with staticNames as the initial value
const names = openBurningForms.reduce(nameGenerator, staticNames) as OpenBurningRouteNames;

const additionalChildRoutes: { [key: string]: RouterOptions["routes"] } = {
  // routes beyond the standard predefined routes should be added here.
  // the key should be the form code, and the value should be an array of routes.
};

const createRoutes = () => {
  const routes: RouterOptions["routes"] = openBurningForms.map((form) => {
    const formRouteNames = names[form];
    const result: RouteRecordRaw = {
      path: LE_CODES.ElectronicForm[form],
      name: LE_CODES.ElectronicForm[form],
      component: {
        render: () => <RouterView />,
      },
      children: [
        {
          path: "new",
          component: () => import(`@/views/electronicForm/openBurningForm/${LE_CODES.ElectronicForm[form]}/Base.vue`),
          redirect: { name: formRouteNames.new },
          // hard code the paths based on what form you're making...
          children: [
            {
              path: ":id(\\d+)?/:typeMurn?",
              name: formRouteNames.new,
              meta: { title: "New Form", isNew: true },
              component: () =>
                import(`@/views/electronicForm/openBurningForm/${LE_CODES.ElectronicForm[form]}/Edit.vue`),
            },
          ],
        },
        {
          path: ":id(\\d+)",
          name: formRouteNames.base,
          component: () => import(`@/views/electronicForm/openBurningForm/${LE_CODES.ElectronicForm[form]}/Base.vue`),
          redirect: { name: formRouteNames.view },
          children: [
            {
              path: "view",
              name: formRouteNames.view,
              meta: { title: "Form View" },
              component: () =>
                import(`@/views/electronicForm/openBurningForm/${LE_CODES.ElectronicForm[form]}/View.vue`),
            },
            {
              path: "edit",
              name: formRouteNames.edit,
              meta: { title: "Form Edit", isForm: true },
              component: () =>
                import(`@/views/electronicForm/openBurningForm/${LE_CODES.ElectronicForm[form]}/Edit.vue`),
            },
            {
              path: "activity",
              name: formRouteNames.audit,
              meta: { title: "Form Audit Activity" },
              component: {
                render: () => <EntityAudit entityType="ElectronicForm" />,
              },
            },
            {
              path: "initiateSubmission",
              name: formRouteNames.initiateSubmission,
              meta: { title: "Submission Agreement", isForm: true },
              ...withProps(() => import("@/components/submission/InitiateSubmission.vue"), {
                formCode: form,
                formRouteNames,
              }),
            },
            {
              // Mirrors the initiateSubmission route for PDF generation.
              path: "submissionAgreementForPdf",
              name: formRouteNames.submissionAgreementForPdf,
              meta: { title: "Submission Agreement", isForm: true },
              ...withProps(() => import("@/components/submission/InitiateSubmission.vue"), {
                formCode: form,
                formRouteNames,
                isSubmissionAgreementForPdf: true,
              }),
            },
            {
              path: "reviewSubmission",
              name: formRouteNames.reviewSubmission,
              meta: { title: "Submission Review", isForm: true },
              ...withProps(() => import("@/components/submission/ReviewSubmission.vue"), {
                formCode: form,
                formRouteNames,
              }),
            },
            {
              path: "repudiateSubmission",
              name: formRouteNames.repudiateSubmission,
              meta: { title: "Submission Repudiation", isForm: true },
              ...withProps(() => import("@/components/submission/RepudiateSubmission.vue"), {
                formCode: form,
                formRouteNames,
              }),
            },
            ...createWorksheetChildRoutes(formRouteNames),
            ...(additionalChildRoutes[form] ? additionalChildRoutes[form] : []),
          ],
        },
      ],
    };
    return result;
  });
  return routes;
};

export { names as openBurningRouteNames };

const openBurningRoutes: RouterOptions["routes"] = [
  {
    path: "/openBurning",
    name: names.openBurning,
    component: {
      render: () => <RouterView />,
    },
    redirect: { name: names.openBurningList },
    children: [
      {
        path: "list",
        name: names.openBurningList,
        meta: {
          title: "Open Burning Forms",
          lookupGroups: ["ElectronicForm", "ElectronicFormType", "ElectronicFormStatus"],
        },
        // props: (route) => ({
        //   filterMurns: route.params.filterMurns,
        // }),
        // beforeEnter: async (to, from, next) => {
        //   try {
        //     const formTypeLookups = await getLookupsFromLookupGroup("ElectronicForm");

        //     to.params.filterMurns = formTypeLookups._items
        //       .filter((lookup) =>
        //         lookup.metadata?.filtersOn?.groupMurn.values.includes(
        //           LE.ElectronicFormGroup.OpenBurning,
        //         ),
        //       )
        //       .map((x) => x.murn);

        //     next();
        //   } catch (error) {
        //     next({ name: "errorRoute" }); //TODO:
        //   }
        // },
        component: () => import("@/views/electronicForm/openBurningForm/OpenBurningList.vue"),
      },
      {
        path: "formSelect",
        name: names.openBurningSelect,
        meta: { title: "Select Open Burning Form", isNew: true },
        component: () => import("@/views/electronicForm/openBurningForm/OpenBurningSelect.vue"),
      },
      ...createRoutes(),
    ],
  },
];
export default openBurningRoutes;
